import { useEffect, useMemo, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import {
  TASK_CENTER_PERMISSIONS,
  hasPermission,
} from '@/lib/taskCenterPermissions';
import {
  useTaskCenter,
  type CreateTaskCenterItemDto,
  type TaskCenterItemDto,
  type TaskCenterPriority,
  type TaskCenterSource,
  type TaskCenterStatus,
} from '@/hooks/useTaskCenter';
import {
  TASK_CENTER_STATUS_LABELS,
  TASK_CENTER_PRIORITY_LABELS,
  TASK_CENTER_SOURCE_LABELS,
  formatEnumLabel,
} from '@/lib/enumLabels';

const STATUSES: readonly TaskCenterStatus[] = [
  'pending',
  'in_progress',
  'deferred',
  'completed',
  'canceled',
];

const PRIORITIES: readonly TaskCenterPriority[] = [
  'low',
  'normal',
  'high',
  'urgent',
  'medical_safety',
];

const SOURCES: readonly TaskCenterSource[] = [
  'crm_stage_followup',
  'proposal_review',
  'payment_confirmation',
  'project_unlock_approval',
  'lab_schedule',
  'sample_receive_confirmation',
  'qc_review',
  'infusion_identity_check',
  'followup_reminder',
  'inventory_warning',
  'compliance_recheck',
  'promotion_review',
  'manual',
];

const PROCESSABLE: ReadonlySet<TaskCenterStatus> = new Set([
  'pending',
  'in_progress',
]);

function fmtDateTime(value?: string): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

const STATUS_TONE: Record<TaskCenterStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  canceled: 'bg-slate-100 text-slate-600',
  deferred: 'bg-purple-100 text-purple-700',
};

const PRIORITY_TONE: Record<TaskCenterPriority, string> = {
  low: 'bg-slate-100 text-slate-600',
  normal: 'bg-blue-50 text-blue-700',
  high: 'bg-yellow-100 text-yellow-700',
  urgent: 'bg-orange-100 text-orange-700',
  medical_safety: 'bg-red-100 text-red-700',
};

const BADGE_BASE = 'inline-flex rounded-full px-2 py-1 text-xs font-medium';

interface CreateFormState {
  title: string;
  source: TaskCenterSource;
  taskType: string;
  businessModule: string;
  priority: TaskCenterPriority;
  assigneeId: string;
  linkedCustomerId: string;
  linkedOrderId: string;
  dueAt: string;
  description: string;
}

const emptyCreateForm: CreateFormState = {
  title: '',
  source: 'manual',
  taskType: 'manual',
  businessModule: 'operations',
  priority: 'normal',
  assigneeId: '',
  linkedCustomerId: '',
  linkedOrderId: '',
  dueAt: '',
  description: '',
};

export default function TaskCenterPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, TASK_CENTER_PERMISSIONS.READ);
  const canCreate = hasPermission(permissions, TASK_CENTER_PERMISSIONS.CREATE);
  const canAssign = hasPermission(permissions, TASK_CENTER_PERMISSIONS.ASSIGN);
  const canTransition = hasPermission(
    permissions,
    TASK_CENTER_PERMISSIONS.TRANSITION,
  );
  const {
    tasks,
    loading,
    error,
    fetchTasks,
    createTask,
    assignTask,
    startTask,
    completeTask,
    cancelTask,
    deferTask,
    reviveTask,
  } = useTaskCenter();

  const [statusFilter, setStatusFilter] = useState<TaskCenterStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TaskCenterPriority | ''>(
    '',
  );
  const [sourceFilter, setSourceFilter] = useState<TaskCenterSource | ''>('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [dueAtFrom, setDueAtFrom] = useState('');
  const [dueAtTo, setDueAtTo] = useState('');

  const [draftAssignee, setDraftAssignee] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [draftDeferUntil, setDraftDeferUntil] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState(0);
  const [createForm, setCreateForm] = useState<CreateFormState>(emptyCreateForm);

  const refresh = useMemo(
    () => () =>
      fetchTasks({
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        source: sourceFilter || undefined,
        assigneeId: assigneeFilter || undefined,
        dueAtFrom: dueAtFrom
          ? new Date(dueAtFrom).toISOString()
          : undefined,
        dueAtTo: dueAtTo ? new Date(dueAtTo).toISOString() : undefined,
      }),
    [
      fetchTasks,
      statusFilter,
      priorityFilter,
      sourceFilter,
      assigneeFilter,
      dueAtFrom,
      dueAtTo,
    ],
  );

  useEffect(() => {
    if (!canRead) return;
    void refresh();
  }, [canRead, refresh]);

  if (!canRead) {
    return <AccessDenied resource="任务中心" />;
  }

  function openCreate() {
    setCreateForm(emptyCreateForm);
    setCreateKey((k) => k + 1);
    setCreateOpen(true);
  }

  function closeCreate() {
    setCreateOpen(false);
  }

  async function handleCreate() {
    if (!createForm.title || !createForm.taskType || !createForm.businessModule) {
      return;
    }
    const body: CreateTaskCenterItemDto = {
      title: createForm.title,
      source: createForm.source,
      taskType: createForm.taskType,
      businessModule: createForm.businessModule,
      priority: createForm.priority,
      ...(createForm.assigneeId ? { assigneeId: createForm.assigneeId } : {}),
      ...(createForm.linkedCustomerId
        ? { linkedCustomerId: createForm.linkedCustomerId }
        : {}),
      ...(createForm.linkedOrderId
        ? { linkedOrderId: createForm.linkedOrderId }
        : {}),
      ...(createForm.description
        ? { description: createForm.description }
        : {}),
      ...(createForm.dueAt
        ? { dueAt: new Date(createForm.dueAt).toISOString() }
        : {}),
    };

    const created = await createTask(body);
    if (created) {
      closeCreate();
      await refresh();
    }
  }

  async function handleAssign(item: TaskCenterItemDto) {
    if (!draftAssignee) return;
    const result = await assignTask(item.id, {
      expectedVersion: item.version,
      assigneeId: draftAssignee,
    });
    if (result) {
      setDraftAssignee('');
      await refresh();
    }
  }

  async function handleStart(item: TaskCenterItemDto) {
    const result = await startTask(item.id, {
      expectedVersion: item.version,
    });
    if (result) await refresh();
  }

  async function handleComplete(item: TaskCenterItemDto) {
    if (!draftNotes) return;
    const result = await completeTask(item.id, {
      expectedVersion: item.version,
      resolutionNotes: draftNotes,
    });
    if (result) {
      setDraftNotes('');
      await refresh();
    }
  }

  async function handleCancel(item: TaskCenterItemDto) {
    if (!draftNotes) return;
    const result = await cancelTask(item.id, {
      expectedVersion: item.version,
      reason: draftNotes,
    });
    if (result) {
      setDraftNotes('');
      await refresh();
    }
  }

  async function handleDefer(item: TaskCenterItemDto) {
    if (!draftDeferUntil) return;
    const result = await deferTask(item.id, {
      expectedVersion: item.version,
      deferredUntil: new Date(draftDeferUntil).toISOString(),
      reason: draftNotes || undefined,
    });
    if (result) {
      setDraftDeferUntil('');
      setDraftNotes('');
      await refresh();
    }
  }

  async function handleRevive(item: TaskCenterItemDto) {
    const result = await reviveTask(item.id, {
      expectedVersion: item.version,
    });
    if (result) await refresh();
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">任务中心</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            统一待办任务中心：客户跟进、方案审核、付款确认、实验室排期、回输身份核对、合规复核等跨模块流程的执行层。
          </p>
        </div>
        {canCreate && (
          <button
            onClick={openCreate}
            className="rounded bg-blue-700 px-4 py-2 text-sm text-white hover:bg-blue-600"
          >
            新建任务
          </button>
        )}
      </div>

      <div className="grid gap-3 rounded-lg border bg-white p-5 shadow-sm lg:grid-cols-3 xl:grid-cols-6">
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as TaskCenterStatus | '')
          }
          className="rounded border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">全部状态</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {formatEnumLabel(s, TASK_CENTER_STATUS_LABELS, '未知状态')}
            </option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) =>
            setPriorityFilter(e.target.value as TaskCenterPriority | '')
          }
          className="rounded border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">全部优先级</option>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {formatEnumLabel(p, TASK_CENTER_PRIORITY_LABELS, '未知优先级')}
            </option>
          ))}
        </select>
        <select
          value={sourceFilter}
          onChange={(e) =>
            setSourceFilter(e.target.value as TaskCenterSource | '')
          }
          className="rounded border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">全部来源</option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {formatEnumLabel(s, TASK_CENTER_SOURCE_LABELS, '未知来源')}
            </option>
          ))}
        </select>
        <input
          className="rounded border border-gray-300 px-3 py-2 text-sm"
          placeholder="按 assigneeId 过滤"
          value={assigneeFilter}
          onChange={(e) => setAssigneeFilter(e.target.value)}
        />
        <input
          type="datetime-local"
          className="rounded border border-gray-300 px-3 py-2 text-sm"
          placeholder="dueAt 起"
          value={dueAtFrom}
          onChange={(e) => setDueAtFrom(e.target.value)}
        />
        <input
          type="datetime-local"
          className="rounded border border-gray-300 px-3 py-2 text-sm"
          placeholder="dueAt 止"
          value={dueAtTo}
          onChange={(e) => setDueAtTo(e.target.value)}
        />
      </div>

      <div className="grid gap-4 rounded-lg border bg-white p-5 shadow-sm lg:grid-cols-3">
        <input
          className="rounded border border-gray-300 px-3 py-2 text-sm"
          placeholder="操作输入 · 分派 assigneeId"
          value={draftAssignee}
          onChange={(e) => setDraftAssignee(e.target.value)}
        />
        <input
          type="datetime-local"
          className="rounded border border-gray-300 px-3 py-2 text-sm"
          placeholder="延期到 deferredUntil"
          value={draftDeferUntil}
          onChange={(e) => setDraftDeferUntil(e.target.value)}
        />
        <textarea
          className="min-h-20 rounded border border-gray-300 px-3 py-2 text-sm lg:col-span-3"
          placeholder="完成 / 取消 / 延期说明（必填）"
          value={draftNotes}
          onChange={(e) => setDraftNotes(e.target.value)}
        />
      </div>

      {error && (
        <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">
                任务
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">
                来源 / 模块
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">
                状态
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">
                优先级
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">
                责任人 / 关联
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">
                时间
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">
                操作
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  加载中…
                </td>
              </tr>
            ) : tasks.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  暂无任务
                </td>
              </tr>
            ) : (
              tasks.map((item) => (
                <tr key={item.id} className="border-t align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium">{item.title}</div>
                    {item.description && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {item.description}
                      </div>
                    )}
                    <div className="mt-1 font-mono text-xs text-gray-500">
                      v{item.version}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div>{formatEnumLabel(item.source, TASK_CENTER_SOURCE_LABELS, '未知来源')}</div>
                    <div className="text-muted-foreground">
                      {item.businessModule} · {item.taskType}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn(BADGE_BASE, STATUS_TONE[item.status])}>
                      {formatEnumLabel(item.status, TASK_CENTER_STATUS_LABELS, '未知状态')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(BADGE_BASE, PRIORITY_TONE[item.priority])}
                    >
                      {formatEnumLabel(item.priority, TASK_CENTER_PRIORITY_LABELS, '未知优先级')}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">
                    <div>assignee={item.assigneeId || '—'}</div>
                    <div>owner={item.ownerUserId}</div>
                    {item.linkedCustomerId && (
                      <div>customer={item.linkedCustomerId}</div>
                    )}
                    {item.linkedOrderId && (
                      <div>order={item.linkedOrderId}</div>
                    )}
                    {item.linkedProjectId && (
                      <div>project={item.linkedProjectId}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    <div>due: {fmtDateTime(item.dueAt)}</div>
                    <div className="text-muted-foreground">
                      created: {fmtDateTime(item.createdAt)}
                    </div>
                    {item.completedAt && (
                      <div className="text-muted-foreground">
                        completed: {fmtDateTime(item.completedAt)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {canAssign && PROCESSABLE.has(item.status) && (
                        <button
                          onClick={() => {
                            void handleAssign(item);
                          }}
                          className="rounded bg-slate-700 px-2 py-1 text-xs text-white hover:bg-slate-600"
                        >
                          分派 / 转交
                        </button>
                      )}
                      {canTransition && item.status === 'pending' && (
                        <button
                          onClick={() => {
                            void handleStart(item);
                          }}
                          className="rounded bg-blue-700 px-2 py-1 text-xs text-white hover:bg-blue-600"
                        >
                          开始
                        </button>
                      )}
                      {canTransition && item.status === 'in_progress' && (
                        <button
                          onClick={() => {
                            void handleComplete(item);
                          }}
                          className="rounded bg-emerald-700 px-2 py-1 text-xs text-white hover:bg-emerald-600"
                        >
                          完成
                        </button>
                      )}
                      {canTransition && PROCESSABLE.has(item.status) && (
                        <button
                          onClick={() => {
                            void handleDefer(item);
                          }}
                          className="rounded bg-purple-700 px-2 py-1 text-xs text-white hover:bg-purple-600"
                        >
                          延期
                        </button>
                      )}
                      {canTransition && item.status === 'deferred' && (
                        <button
                          onClick={() => {
                            void handleRevive(item);
                          }}
                          className="rounded bg-blue-700 px-2 py-1 text-xs text-white hover:bg-blue-600"
                        >
                          唤回
                        </button>
                      )}
                      {canTransition &&
                        (PROCESSABLE.has(item.status) ||
                          item.status === 'deferred') && (
                          <button
                            onClick={() => {
                              void handleCancel(item);
                            }}
                            className="rounded bg-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-300"
                          >
                            取消
                          </button>
                        )}
                    </div>
                    {(item.resolutionNotes || item.assigneeId) && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        {item.resolutionNotes || '无处理说明'}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <div
          key={createKey}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        >
          <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold">新建任务</h2>
            <div className="grid gap-3 lg:grid-cols-2">
              <label className="text-sm">
                标题
                <input
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={createForm.title}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, title: e.target.value })
                  }
                  required
                />
              </label>
              <label className="text-sm">
                来源
                <select
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={createForm.source}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      source: e.target.value as TaskCenterSource,
                    })
                  }
                >
                  {SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {formatEnumLabel(s, TASK_CENTER_SOURCE_LABELS, '未知来源')}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                任务类型
                <input
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={createForm.taskType}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, taskType: e.target.value })
                  }
                  required
                />
              </label>
              <label className="text-sm">
                业务模块
                <input
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={createForm.businessModule}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      businessModule: e.target.value,
                    })
                  }
                  required
                />
              </label>
              <label className="text-sm">
                优先级
                <select
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={createForm.priority}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      priority: e.target.value as TaskCenterPriority,
                    })
                  }
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {formatEnumLabel(p, TASK_CENTER_PRIORITY_LABELS, '未知优先级')}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                责任人 assigneeId（可选）
                <input
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={createForm.assigneeId}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      assigneeId: e.target.value,
                    })
                  }
                />
              </label>
              <label className="text-sm">
                关联客户 customerId（可选）
                <input
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={createForm.linkedCustomerId}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      linkedCustomerId: e.target.value,
                    })
                  }
                />
              </label>
              <label className="text-sm">
                关联订单 orderId（可选）
                <input
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={createForm.linkedOrderId}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      linkedOrderId: e.target.value,
                    })
                  }
                />
              </label>
              <label className="text-sm">
                截止时间
                <input
                  type="datetime-local"
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={createForm.dueAt}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, dueAt: e.target.value })
                  }
                />
              </label>
              <label className="text-sm lg:col-span-2">
                描述
                <textarea
                  className="mt-1 min-h-20 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={createForm.description}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      description: e.target.value,
                    })
                  }
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={closeCreate}
                className="rounded border border-gray-300 px-4 py-2 text-sm"
              >
                取消
              </button>
              <button
                onClick={() => {
                  void handleCreate();
                }}
                className="rounded bg-blue-700 px-4 py-2 text-sm text-white hover:bg-blue-600"
              >
                创建
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
